/**
 * /terms — Terms of Service
 *
 * Publicly accessible, no authentication required.
 * Governed by the laws of British Columbia, Canada.
 *
 * ⚠️  LEGAL NOTICE: This draft has been prepared for review purposes.
 *     Have qualified legal counsel in British Columbia review and
 *     approve this document before public launch.
 */
import { Link } from "react-router-dom";
import { KeyRound, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { PublicFooter } from "@/components/PublicFooter";

const EFFECTIVE_DATE = "March 5, 2026";
const COMPANY_NAME = "Coastline Digital Solutions";
const COMPANY_SHORT = "Stratakey";
const CONTACT_EMAIL = "info@stratakey.ca";

export default function TermsOfService() {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Nav bar */}
      <header className="sticky top-0 z-10 flex h-14 items-center gap-3 border-b bg-background/95 px-6 backdrop-blur">
        <Link to="/" className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary">
            <KeyRound className="h-4 w-4 text-primary-foreground" />
          </div>
          <span className="font-semibold text-sm">{COMPANY_SHORT}</span>
        </Link>
        <Separator orientation="vertical" className="h-4" />
        <span className="text-sm text-muted-foreground">Terms of Service</span>
        <div className="ml-auto">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/login">
              <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
              Back
            </Link>
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 py-10">
        <div className="mx-auto max-w-3xl px-6">
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight">Terms of Service</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Effective date: {EFFECTIVE_DATE}
            </p>
          </div>

          <div className="prose prose-sm prose-neutral dark:prose-invert max-w-none space-y-8 text-sm leading-relaxed text-foreground">

            {/* 1 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">1. Acceptance of Terms</h2>
              <p>
                By accessing or using the Stratakey STR Compliance Platform
                (the "<strong>Service</strong>"), you agree to be bound by these Terms of
                Service ("<strong>Terms</strong>"). If you do not agree to all of these Terms,
                do not use the Service. These Terms constitute a legally binding agreement
                between you and {COMPANY_NAME} ("<strong>Stratakey</strong>," "<strong>we</strong>,"
                "<strong>us</strong>," or "<strong>our</strong>"), a corporation registered in
                British Columbia, Canada.
              </p>
              <p className="mt-3">
                We may update these Terms from time to time. Continued use of the Service
                after notice of changes constitutes your acceptance of the revised Terms.
                We will notify registered users of material changes by email or in-app notice
                at least 14 days in advance.
              </p>
            </section>

            <Separator />

            {/* 2 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">2. Description of Service</h2>
              <p>
                Stratakey is a cloud-based platform designed to help strata corporations,
                property managers, and short-term rental (STR) hosts comply with the
                <em> BC Short-Term Rental Accommodations Act</em> (SBC 2024, c.11) and related
                municipal bylaws. The Service includes:
              </p>
              <ul className="list-disc pl-5 mt-3 space-y-1">
                <li>STR host registration and BC Registry permit verification</li>
                <li>Automated booking validation against night limits and strata bylaws</li>
                <li>Guest check-in portal with ID upload and building-rules acknowledgement</li>
                <li>Compliance reporting, fine management, and audit logging</li>
                <li>Notifications, daily digests, and team management for strata councils</li>
              </ul>
              <p className="mt-3">
                The Service is available to users worldwide.
                Nothing in the Service constitutes legal advice. Compliance with all
                applicable laws remains the sole responsibility of the subscribing organization.
              </p>
            </section>

            <Separator />

            {/* 3 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">3. Accounts and User Roles</h2>
              <p>
                Access to the Service is role-based. The following roles are defined:
              </p>
              <ul className="list-disc pl-5 mt-3 space-y-1">
                <li><strong>Strata Council Admin:</strong> Manages the strata organization, configures bylaws, approves hosts, and reviews bookings.</li>
                <li><strong>Property Manager:</strong> Manages one or more strata buildings on behalf of strata councils.</li>
                <li><strong>STR Host:</strong> A unit owner or authorized operator who registers short-term rental bookings.</li>
                <li><strong>Guest:</strong> A temporary visitor who accesses a read-only check-in portal via a one-time token. Guests do not create accounts.</li>
              </ul>
              <p className="mt-3">
                You are responsible for maintaining the confidentiality of your account credentials and
                for all activity that occurs under your account. You must notify us immediately at{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary underline">{CONTACT_EMAIL}</a>{" "}
                of any unauthorized use.
              </p>
              <p className="mt-3">
                You must be at least 19 years of age to create an account. By creating an
                account you represent that all information you provide is accurate and current.
              </p>
            </section>

            <Separator />

            {/* 4 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">4. Subscriptions and Payment</h2>
              <p>
                Access to certain features requires a paid subscription. Subscriptions are billed
                monthly or annually in Canadian dollars (CAD) via Stripe, our third-party payment
                processor. By subscribing you authorize us to charge your payment method on a
                recurring basis until you cancel.
              </p>
              <ul className="list-disc pl-5 mt-3 space-y-2">
                <li>
                  <strong>Free trial:</strong> Where offered, free trials automatically convert to
                  a paid subscription at the end of the trial period unless cancelled beforehand.
                </li>
                <li>
                  <strong>Cancellation:</strong> You may cancel at any time through the Billing
                  page. Cancellations take effect immediately and a prorated refund for unused days
                  will be issued to your original payment method within 5–10 business days.
                </li>
                <li>
                  <strong>Price changes:</strong> We will provide at least 30 days' notice of
                  price changes by email. Continued use after the price change takes effect
                  constitutes acceptance of the new price.
                </li>
                <li>
                  <strong>Taxes:</strong> Prices are exclusive of applicable taxes, including
                  GST/HST, which will be added at checkout where required by law.
                </li>
              </ul>
            </section>

            <Separator />

            {/* 5 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">5. Acceptable Use</h2>
              <p>You agree not to:</p>
              <ul className="list-disc pl-5 mt-3 space-y-1">
                <li>Use the Service to circumvent or obscure STR bylaw obligations</li>
                <li>Submit false or misleading registration numbers or guest information</li>
                <li>Attempt to gain unauthorized access to other organizations' data</li>
                <li>Reverse-engineer, scrape, or resell any part of the Service</li>
                <li>Transmit malware, spam, or any illegal content through the Service</li>
                <li>Use the Service in any manner that violates applicable law, including
                  the <em>BC Personal Information Protection Act</em> (PIPA) and the
                  <em> Freedom of Information and Protection of Privacy Act</em> (FOIPPA)</li>
              </ul>
              <p className="mt-3">
                Violation of this section may result in immediate account suspension or
                termination without refund.
              </p>
            </section>

            <Separator />

            {/* 6 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">6. Intellectual Property</h2>
              <p>
                The Service, including its software, design, trademarks, and content, is owned
                by {COMPANY_NAME} and protected by Canadian and international intellectual property
                laws. These Terms do not grant you any rights in our intellectual property beyond
                the limited licence to use the Service as described herein.
              </p>
              <p className="mt-3">
                You retain ownership of all data you submit to the Service ("<strong>Your Data</strong>").
                You grant {COMPANY_SHORT} a limited, non-exclusive licence to host, store, and
                process Your Data solely to provide and improve the Service.
              </p>
            </section>

            <Separator />

            {/* 7 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">7. Disclaimer of Warranties</h2>
              <p>
                THE SERVICE IS PROVIDED "<strong>AS IS</strong>" AND "<strong>AS AVAILABLE</strong>"
                WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO
                WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
                NON-INFRINGEMENT. WE DO NOT WARRANT THAT:
              </p>
              <ul className="list-disc pl-5 mt-3 space-y-1">
                <li>The Service will be uninterrupted, error-free, or secure at all times</li>
                <li>Any defects will be corrected within a specific timeframe</li>
                <li>The Service ensures full regulatory compliance for your specific situation</li>
                <li>BC Registry data returned by the Service is current or accurate (data is
                  sourced from the BC Registries API)</li>
              </ul>
            </section>

            <Separator />

            {/* 8 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">8. Limitation of Liability</h2>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, {COMPANY_NAME.toUpperCase()} AND
                ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY
                INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF
                YOUR USE OF OR INABILITY TO USE THE SERVICE, INCLUDING BUT NOT LIMITED TO LOSS OF
                PROFITS, DATA, GOODWILL, OR REGULATORY FINES, EVEN IF WE HAVE BEEN ADVISED OF THE
                POSSIBILITY OF SUCH DAMAGES.
              </p>
              <p className="mt-3">
                OUR TOTAL CUMULATIVE LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM OR RELATING TO
                THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID
                TO US IN THE 12 MONTHS PRECEDING THE CLAIM OR (B) CAD $100.
              </p>
            </section>

            <Separator />

            {/* 9 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">9. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless {COMPANY_NAME}, its affiliates, officers,
                directors, employees, and agents from and against any claims, liabilities, damages,
                losses, and expenses (including reasonable legal fees) arising out of or in any way
                connected with: (a) your access to or use of the Service; (b) your violation of
                these Terms; (c) your violation of any applicable law or regulation; or (d) any
                data you submit to the Service.
              </p>
            </section>

            <Separator />

            {/* 10 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">10. Termination</h2>
              <p>
                Either party may terminate these Terms at any time. We may suspend or terminate
                your account with immediate effect if you violate these Terms, fail to pay
                subscription fees, or engage in conduct that we reasonably believe is harmful
                to the Service, other users, or third parties.
              </p>
              <p className="mt-3">
                Upon termination, your right to access the Service ceases immediately. You may
                request a copy of Your Data within 30 days of termination. After 90 days,
                we may delete Your Data in accordance with our Privacy Policy.
              </p>
            </section>

            <Separator />

            {/* 11 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">11. Governing Law and Dispute Resolution</h2>
              <p>
                These Terms are governed by and construed in accordance with the laws of the
                Province of British Columbia and the federal laws of Canada applicable therein,
                without regard to conflict-of-law principles. Any dispute arising under these
                Terms shall be resolved exclusively in the courts of British Columbia, Canada,
                and you consent to the personal jurisdiction of such courts.
              </p>
              <p className="mt-3">
                Before initiating formal legal proceedings, you agree to first contact us at{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary underline">{CONTACT_EMAIL}</a>{" "}
                to attempt to resolve the dispute informally. We will endeavour to resolve disputes
                within 30 days.
              </p>
            </section>

            <Separator />

            {/* 12 */}
            <section>
              <h2 className="text-lg font-semibold mb-3">12. Contact</h2>
              <p>
                For questions about these Terms, please contact us:
              </p>
              <address className="mt-3 not-italic space-y-1 text-muted-foreground">
                <p><strong className="text-foreground">{COMPANY_NAME}</strong></p>
                <p>British Columbia, Canada</p>
                <p>
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary underline">
                    {CONTACT_EMAIL}
                  </a>
                </p>
              </address>
            </section>

          </div>

          {/* Back link */}
          <div className="mt-12 flex items-center gap-3">
            <Button variant="outline" asChild>
              <Link to="/privacy">
                View Privacy Policy →
              </Link>
            </Button>
            <Button variant="ghost" asChild>
              <Link to="/login">
                <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
                Back to app
              </Link>
            </Button>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
